import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  ClockIcon,
  CurrencyEuroIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { PaymentTable } from './components/PaymentTable';
import { PaymentFilters } from './components/PaymentFilters';
import { usePayments } from './hooks/usePayments';
import { usePaymentNotifications } from './hooks/usePaymentNotifications';
import { useSyncStatus } from './hooks/useSyncStatus';
import { useAffairePaymentStats } from './hooks/useAffairePaymentStats';
import { formatRelativeTime, formatNextSyncTime } from './utils/formatRelativeTime';
import type { PaymentFilters as PaymentFiltersType } from './types';
import './styles/payment.css';

export const PaymentTab: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Restore scroll position when returning from tracker
  useEffect(() => {
    if (location.state?.scrollPosition !== undefined) {
      // Small delay to ensure content is rendered
      requestAnimationFrame(() => {
        window.scrollTo(0, location.state.scrollPosition);
      });
      // Clear the state to prevent re-scrolling on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Enable payment notifications (WebSocket events)
  usePaymentNotifications();

  // Fetch banking sync status
  const { data: syncStatus } = useSyncStatus();

  // Fetch affaire payment stats (all-time, for multi-payment indicator)
  const { stats: affairePaymentStats } = useAffairePaymentStats();

  // Set default date to 3 days ago
  const getDefaultDateFrom = () => {
    const date = new Date();
    date.setDate(date.getDate() - 3);
    return format(date, 'yyyy-MM-dd');
  };
  
  const [filters, setFilters] = useState<PaymentFiltersType>({
    status: 'all',
    search: '',
    dateFrom: getDefaultDateFrom(),
    dateTo: undefined
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Fetch all payments for statistics (unfiltered by status/search)
  const {
    payments: allPayments,
    isLoading: isLoadingStats,
  } = usePayments({ 
    status: 'all', 
    search: '',
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo
  });

  // Fetch filtered payments for display
  const {
    payments,
    isLoading,
    error,
    refetch,
    updateAffaire,
    validatePayment,
    ignorePayment,
    isUpdating,
    isValidating,
    isIgnoring,
    updateError,
    validateError,
    ignoreError,
  } = usePayments(filters);
  
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleUpdateAffaire = (paymentId: number, affaireId: string) => {
    updateAffaire({ payment_id: paymentId, affaire_id: affaireId });
  };

  const handleValidatePayment = (paymentId: number, validate: boolean) => {
    validatePayment({ payment_id: paymentId, validated: validate });
  };

  const handleIgnorePayment = (paymentId: number, isIgnored: boolean) => {
    ignorePayment({ paymentId, isIgnored });
  };

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      setNotification({ 
        type: 'success', 
        message: 'Données rafraîchies avec succès' 
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      setNotification({ 
        type: 'error', 
        message: 'Erreur lors du rafraîchissement des données' 
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Show notifications based on mutation results
  React.useEffect(() => {
    if (updateError || validateError || ignoreError) {
      const error = updateError || validateError || ignoreError;
      setNotification({ 
        type: 'error', 
        message: (error as any)?.response?.data?.message || 'Une erreur est survenue' 
      });
      setTimeout(() => setNotification(null), 5000);
    }
  }, [updateError, validateError, ignoreError]);

  // Filter payments based on filters
  const filteredPayments = React.useMemo(() => {
    let result = [...payments];

    // Filter by status
    if (filters.status !== 'all') {
      switch (filters.status) {
        case 'validated':
          // Only show truly paid transactions (PAYE BANQUE)
          result = result.filter(p => 
            !p.is_ignored && 
            (p.payment_status === 'PAYE BANQUE' || 
             (p.affaires && p.affaires.length > 0 && p.affaires.every(a => a.payment_status === 'PAYE BANQUE')))
          );
          break;
        case 'pending':
          // Show transactions with affaires but not paid
          result = result.filter(p => 
            !p.is_ignored && 
            (p.affaire_id || (p.affaires && p.affaires.length > 0)) && 
            p.payment_status !== 'PAYE BANQUE' &&
            !p.affaires?.every(a => a.payment_status === 'PAYE BANQUE')
          );
          break;
        case 'unmatched':
          // Show transactions without any affaire
          result = result.filter(p => 
            !p.is_ignored && 
            !p.affaire_id && 
            (!p.affaires || p.affaires.length === 0)
          );
          break;
        case 'ignored':
          // Show only ignored transactions
          result = result.filter(p => p.is_ignored);
          break;
      }
    }

    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.trim();

      // Check if search term is a 4-7 digit number (affaire number format)
      const isAffaireSearch = /^\d{4,7}$/.test(searchTerm);

      if (isAffaireSearch) {
        // Only search in affaire-related fields (not communication to avoid false positives)
        result = result.filter(p =>
          p.affaire_id?.includes(searchTerm) ||
          p.suggested_affaire_id?.includes(searchTerm) ||
          p.affaires?.some(a => a.affaire_id?.includes(searchTerm))
        );
      } else {
        // For text searches, keep current behavior (name + communication + affaires)
        const searchLower = searchTerm.toLowerCase();
        result = result.filter(p =>
          p.name?.toLowerCase().includes(searchLower) ||
          p.communication?.toLowerCase().includes(searchLower) ||
          p.affaire_id?.toLowerCase().includes(searchLower)
        );
      }
    }

    // Date filtering is handled server-side, no need for client-side filtering
    // The server filters using booking_date which is more accurate

    return result;
  }, [payments, filters]);

  if (error) {
    return (
      <div className="payment-page-container">
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-medium">Erreur de chargement</h3>
            <p className="text-red-600 mt-1">
              Impossible de charger les paiements. Veuillez réessayer.
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page-container">
      {/* Hero Section - Matching ticket tab style */}
      <div className="payment-hero">
        <div className="payment-hero-content">
          <h1>{t('admin.payment.title', 'Gestion des Paiements')}</h1>
          <p>{t('admin.payment.subtitle', 'Gérez et validez les paiements bancaires')}</p>
          {/* Sync Status Info */}
          {syncStatus && (
            <div className="payment-sync-status">
              <span className="payment-sync-item">
                <ClockIcon className="w-4 h-4" />
                Dernière sync: {formatRelativeTime(syncStatus.lastSync)}
              </span>
              <span className="payment-sync-separator">•</span>
              <span className="payment-sync-item">
                Prochaine: {formatNextSyncTime(syncStatus.nextSync)}
              </span>
            </div>
          )}
        </div>
        <div className="payment-hero-decoration">
          <div className="payment-hero-circle payment-hero-circle-1"></div>
          <div className="payment-hero-circle payment-hero-circle-2"></div>
          <div className="payment-hero-circle payment-hero-circle-3"></div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg border ${
          notification.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'
        }`}>
          <div className="flex items-center">
            <span className="text-sm font-medium">{notification.message}</span>
            <button 
              onClick={() => setNotification(null)}
              className="ml-4 text-sm hover:opacity-75 transition-opacity"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="payment-content">
        {/* Summary Stats - Matching monday-meeting-stats */}
        <div className="payment-stats">
          {/* Total */}
          <div
            className={`payment-stat-card ${filters.status === 'all' ? 'active' : ''}`}
            onClick={() => setFilters({ ...filters, status: 'all' })}
          >
            <div className="payment-stat-icon total">
              <CurrencyEuroIcon className="w-5 h-5" />
            </div>
            <div className="payment-stat-info">
              <span className="payment-stat-value">{allPayments.length}</span>
              <span className="payment-stat-label">Total</span>
            </div>
          </div>

          {/* Non alloués */}
          <div
            className={`payment-stat-card ${filters.status === 'unmatched' ? 'active' : ''}`}
            onClick={() => setFilters({ ...filters, status: 'unmatched' })}
          >
            <div className="payment-stat-icon unmatched">
              <ExclamationCircleIcon className="w-5 h-5" />
            </div>
            <div className="payment-stat-info">
              <span className="payment-stat-value">
                {allPayments.filter(p => !p.is_ignored && !p.affaire_id && (!p.affaires || p.affaires.length === 0)).length}
              </span>
              <span className="payment-stat-label">Non alloués</span>
            </div>
          </div>

          {/* Partiellement */}
          <div
            className={`payment-stat-card ${filters.status === 'pending' ? 'active' : ''}`}
            onClick={() => setFilters({ ...filters, status: 'pending' })}
          >
            <div className="payment-stat-icon pending">
              <ClockIcon className="w-5 h-5" />
            </div>
            <div className="payment-stat-info">
              <span className="payment-stat-value">
                {allPayments.filter(p =>
                  !p.is_ignored &&
                  (p.affaire_id || (p.affaires && p.affaires.length > 0)) &&
                  p.payment_status !== 'PAYE BANQUE' &&
                  !p.affaires?.every(a => a.payment_status === 'PAYE BANQUE')
                ).length}
              </span>
              <span className="payment-stat-label">Partiellement</span>
            </div>
          </div>

          {/* Validés */}
          <div
            className={`payment-stat-card ${filters.status === 'validated' ? 'active' : ''}`}
            onClick={() => setFilters({ ...filters, status: 'validated' })}
          >
            <div className="payment-stat-icon validated">
              <CheckCircleIcon className="w-5 h-5" />
            </div>
            <div className="payment-stat-info">
              <span className="payment-stat-value">
                {allPayments.filter(p =>
                  !p.is_ignored &&
                  (p.payment_status === 'PAYE BANQUE' ||
                   (p.affaires && p.affaires.length > 0 && p.affaires.every(a => a.payment_status === 'PAYE BANQUE')))
                ).length}
              </span>
              <span className="payment-stat-label">Validés</span>
            </div>
          </div>

          {/* Ignorés */}
          <div
            className={`payment-stat-card ${filters.status === 'ignored' ? 'active' : ''}`}
            onClick={() => setFilters({ ...filters, status: 'ignored' as any })}
          >
            <div className="payment-stat-icon ignored">
              <EyeSlashIcon className="w-5 h-5" />
            </div>
            <div className="payment-stat-info">
              <span className="payment-stat-value">{allPayments.filter(p => p.is_ignored).length}</span>
              <span className="payment-stat-label">Ignorés</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <PaymentFilters
          filters={filters}
          onFiltersChange={setFilters}
        />

        {/* Payment Table */}
        <div>
          <PaymentTable
            payments={filteredPayments}
            affairePaymentStats={affairePaymentStats}
            onUpdateAffaire={handleUpdateAffaire}
            onValidate={handleValidatePayment}
            onIgnore={handleIgnorePayment}
            isLoading={isLoading || isUpdating || isValidating || isIgnoring || isRefreshing}
            onRefresh={handleRefreshData}
            onFilterByAffaire={(affaireId) => setFilters({ ...filters, search: affaireId })}
          />
        </div>
      </div>
    </div>
  );
};